import React from 'react';
import {useSelector, useDispatch} from 'react-redux';
import SettingsPanel from './SettingsPanel';
import CustomSelect from './CustomSelect.jsx';
import {
    setOperation,
    setMainOrganization,
    setSecondaryOrganization,
    setTertiaryOrganization,
    setIncludeMonth,
    setDateFormat,
    setAppendToBeginning
} from '../redux/slices/organizerSettingsSlice';

const OrganizerSettings = () => {
    const dispatch = useDispatch();
    const {
        operation,
        mainOrganization,
        secondaryOrganization,
        tertiaryOrganization,
        includeMonth,
        dateFormat,
        appendToBeginning
    } = useSelector((state) => state.organizerSettings);

    // Handler for main organization change
    const handleMainOrganizationChange = (newValue) => {
        // If the new main value is the same as current secondary, set secondary to 'none'
        if (newValue === secondaryOrganization) {
            dispatch(setSecondaryOrganization('none'));
        }
        // If the new main value is the same as current tertiary, set tertiary to 'none'
        if (newValue === tertiaryOrganization) {
            dispatch(setTertiaryOrganization('none'));
        }
        dispatch(setMainOrganization(newValue));
    };

    // Handler for secondary organization change
    const handleSecondaryOrganizationChange = (newValue) => {
        // If the new secondary value is the same as current tertiary, set tertiary to 'none'
        if (newValue === tertiaryOrganization && newValue !== 'none') {
            dispatch(setTertiaryOrganization('none'));
        }
        dispatch(setSecondaryOrganization(newValue));
    };

    // Build settings array
    const settings = [
        {
            customComponent: (
                <div key="operation-setting" className="organizer-setting-item first-setting">
                    <label>Operation</label>
                    <CustomSelect
                        value={operation}
                        onChange={(e) => dispatch(setOperation(e.target.value))}
                        className="organizer-select"
                        options={[
                            { value: 'organize', label: 'Organize into Folders' },
                            { value: 'appendNames', label: 'Append to Filenames' }
                        ]}
                    />
                </div>
            )
        },
        {
            customComponent: (
                <div key="main-org-setting" className="organizer-setting-item">
                    <label>Main Organization</label>
                    <CustomSelect
                        value={mainOrganization}
                        onChange={(e) => handleMainOrganizationChange(e.target.value)}
                        className="organizer-select"
                        options={[
                            {
                                value: 'releaseDate',
                                label: 'Release Date'
                            },
                            {
                                // Internal value stays 'style' (persisted in saved
                                // settings + threaded through organizer.js); the label
                                // says Genre to match the rest of the app and DJ software.
                                value: 'style',
                                label: 'Genre'
                            },
                            {
                                value: 'label',
                                label: 'Label'
                            }
                        ]}
                    />
                </div>
            )
        },
        {
            customComponent: (
                <div key="secondary-org-setting" className="organizer-setting-item">
                    <label>Secondary Organization</label>
                    <CustomSelect
                        value={secondaryOrganization}
                        onChange={(e) => handleSecondaryOrganizationChange(e.target.value)}
                        className="organizer-select"
                        options={[
                            { value: 'none', label: 'None' },
                            {
                                value: 'releaseDate',
                                label: 'Release Date',
                                disabled: mainOrganization === 'releaseDate'
                            },
                            {
                                value: 'style',
                                label: 'Genre',
                                disabled: mainOrganization === 'style'
                            },
                            {
                                value: 'label',
                                label: 'Label',
                                disabled: mainOrganization === 'label'
                            }
                        ]}
                    />
                </div>
            )
        },
        {
            customComponent: (
                <div key="tertiary-org-setting" className="organizer-setting-item">
                    <label>Tertiary Organization</label>
                    <CustomSelect
                        value={tertiaryOrganization}
                        onChange={(e) => dispatch(setTertiaryOrganization(e.target.value))}
                        className="organizer-select"
                        options={[
                            { value: 'none', label: 'None' },
                            {
                                value: 'releaseDate',
                                label: 'Release Date',
                                disabled: mainOrganization === 'releaseDate' || secondaryOrganization === 'releaseDate'
                            },
                            {
                                value: 'style',
                                label: 'Genre',
                                disabled: mainOrganization === 'style' || secondaryOrganization === 'style'
                            },
                            {
                                value: 'label',
                                label: 'Label',
                                disabled: mainOrganization === 'label' || secondaryOrganization === 'label'
                            }
                        ]}
                    />
                </div>
            )
        }
    ];

    // Add operation-specific settings
    if (operation === 'organize') {
        settings.push({
            id: 'includeMonth',
            selector: 'includeMonth',
            label: 'Include Month in Folders',
            action: setIncludeMonth
        });
    }

    if (operation === 'appendNames') {
        settings.push({
            customComponent: (
                <div key="date-format-setting" className="organizer-setting-item">
                    <label>Date Format</label>
                    <CustomSelect
                        value={dateFormat}
                        onChange={(e) => dispatch(setDateFormat(e.target.value))}
                        className="organizer-select"
                        options={[
                            { value: 'year', label: 'Year Only' },
                            { value: 'monthAndYear', label: 'Month and Year' },
                            { value: 'fullDate', label: 'Full Date' }
                        ]}
                    />
                </div>
            )
        });
        settings.push({
            id: 'appendToBeginning',
            selector: 'appendToBeginning',
            label: 'Append at Beginning',
            action: setAppendToBeginning
        });
    }

    return (
        <SettingsPanel
            filtersTitle={null}
            filtersComponent={null}
            title="Settings"
            settings={settings}
            sliceSelector={(state) => state.organizerSettings}
        />
    );
};

export default OrganizerSettings;
